import React, { useState, useEffect } from "react";
import {
  listSeasons,
  getActiveSeason,
  createSeason,
  listLeaguePlayers,
  addLeaguePlayer,
  updateLeaguePlayer,
  removeLeaguePlayer,
  transferPlayer,
  type LeagueSeason,
  type LeaguePlayer,
} from "../../store/leagueSeasons";
import type { LeagueTeam } from "../../store/leagues";

interface Props {
  leagueId: string;
  teams: LeagueTeam[];
}

export default function LeaguePlayersTab({ leagueId, teams }: Props) {
  const [seasons, setSeasons] = useState<LeagueSeason[]>([]);
  const [selectedSeason, setSelectedSeason] = useState<LeagueSeason | null>(null);
  const [players, setPlayers] = useState<LeaguePlayer[]>([]);
  const [filterTeamId, setFilterTeamId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [showAddPlayer, setShowAddPlayer] = useState(false);
  const [showCreateSeason, setShowCreateSeason] = useState(false);

  useEffect(() => {
    loadSeasons();
  }, [leagueId]);

  useEffect(() => {
    if (selectedSeason) loadPlayers();
  }, [selectedSeason, filterTeamId]);

  async function loadSeasons() {
    setLoading(true);
    const data = await listSeasons(leagueId);
    setSeasons(data);
    if (data.length > 0) {
      const active = data.find(s => s.is_active) || data[0];
      setSelectedSeason(active);
    }
    setLoading(false);
  }

  async function loadPlayers() {
    if (!selectedSeason) return;
    const data = await listLeaguePlayers(selectedSeason.id, filterTeamId || undefined);
    setPlayers(data);
  }

  async function handleCreateSeason(name: string) {
    const created = await createSeason(leagueId, name);
    if (created) {
      await loadSeasons();
      setSelectedSeason(created);
      setShowCreateSeason(false);
    }
  }

  async function handleAddPlayer(playerData: Omit<LeaguePlayer, "id" | "created_at" | "updated_at">) {
    const added = await addLeaguePlayer(playerData);
    if (added) {
      await loadPlayers();
      setShowAddPlayer(false);
    }
  }

  async function handleRemovePlayer(playerId: string) {
    if (!confirm("Remove this player from the season?")) return;
    await removeLeaguePlayer(playerId);
    await loadPlayers();
  }

  async function handleTransfer(playerId: string, newTeamId: string) {
    await transferPlayer(playerId, newTeamId);
    await loadPlayers();
  }

  if (loading) {
    return (
      <div className="space-y-3 animate-pulse">
        <div className="h-10 bg-[#0b5c33]/30 rounded-lg" />
        <div className="h-40 bg-[#0b5c33]/20 rounded-xl" />
      </div>
    );
  }

  if (seasons.length === 0) {
    return (
      <div className="bg-[#064428]/40 rounded-xl p-10 text-center border border-[#0b5c33]/50">
        <div className="w-14 h-14 rounded-full bg-[#0f9d3d]/10 border border-[#0f9d3d]/30 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-[#0f9d3d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-lg font-bold text-white mb-2">Create a Season First</h3>
        <p className="text-green-300/70 text-sm mb-5">
          Create a season to start adding players to your league teams.
        </p>
        <button
          onClick={() => setShowCreateSeason(true)}
          className="bg-[#0f9d3d] hover:bg-green-600 px-5 py-2.5 rounded-lg font-semibold text-white text-sm transition-colors"
        >
          Create Season
        </button>
        {showCreateSeason && <CreateSeasonModal onClose={() => setShowCreateSeason(false)} onCreate={handleCreateSeason} />}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Season selector + filters */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={selectedSeason?.id || ""}
          onChange={(e) => setSelectedSeason(seasons.find(s => s.id === e.target.value) || null)}
          className="bg-[#0b5c33]/40 border border-[#0b5c33] text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0f9d3d]"
        >
          {seasons.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select
          value={filterTeamId}
          onChange={(e) => setFilterTeamId(e.target.value)}
          className="bg-[#0b5c33]/40 border border-[#0b5c33] text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0f9d3d]"
        >
          <option value="">All Teams</option>
          {teams.map(t => <option key={t.id} value={t.id}>{t.team_name}</option>)}
        </select>
        <div className="flex-1" />
        <button
          onClick={() => setShowCreateSeason(true)}
          className="text-green-300/60 hover:text-green-300 text-xs px-2 py-1.5 hover:bg-[#0b5c33]/30 rounded transition-colors"
        >
          + Season
        </button>
        <button
          onClick={() => setShowAddPlayer(true)}
          className="bg-[#0f9d3d] hover:bg-green-600 text-white px-3 py-2 rounded-lg font-semibold text-sm transition-colors"
        >
          + Add Player
        </button>
      </div>

      {/* Player list */}
      {players.length === 0 ? (
        <div className="bg-[#064428]/30 rounded-xl p-8 text-center border border-[#0b5c33]/30">
          <p className="text-green-300/60 text-sm mb-3">No players added to this season yet.</p>
          <button
            onClick={() => setShowAddPlayer(true)}
            className="text-[#0f9d3d] text-sm font-semibold hover:underline"
          >
            Add First Player
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {teams.filter(t => !filterTeamId || t.id === filterTeamId).map(team => {
            const teamPlayers = players.filter(p => p.team_id === team.id);
            if (teamPlayers.length === 0) return null;
            return (
              <div key={team.id} className="bg-[#064428]/40 rounded-xl border border-[#0b5c33]/50 overflow-hidden">
                <div className="px-4 py-2.5 bg-[#0b5c33]/30 flex items-center gap-2">
                  <span className="text-white text-sm font-semibold">{team.team_name}</span>
                  <span className="text-green-300/50 text-xs">({teamPlayers.length} players)</span>
                </div>
                <div className="divide-y divide-[#0b5c33]/20">
                  {teamPlayers.map(player => (
                    <div key={player.id} className="px-4 py-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#0f9d3d]/20 flex items-center justify-center flex-shrink-0 text-xs font-bold text-[#0f9d3d]">
                        {player.jersey_number || player.player_name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white text-sm font-medium truncate">{player.player_name}</span>
                          {player.is_captain && <span className="text-[10px] bg-yellow-600/30 text-yellow-300 px-1.5 py-0.5 rounded font-bold">C</span>}
                          {player.is_vice_captain && <span className="text-[10px] bg-blue-600/30 text-blue-300 px-1.5 py-0.5 rounded font-bold">VC</span>}
                          {player.is_wicket_keeper && <span className="text-[10px] bg-green-600/30 text-green-300 px-1.5 py-0.5 rounded font-bold">WK</span>}
                        </div>
                        <span className="text-green-300/50 text-xs">{player.role} | {player.batting_style}</span>
                      </div>
                      <button
                        onClick={() => handleRemovePlayer(player.id)}
                        className="text-red-400/50 hover:text-red-400 p-1 hover:bg-red-900/20 rounded transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modals */}
      {showAddPlayer && selectedSeason && (
        <AddPlayerModal
          leagueId={leagueId}
          seasonId={selectedSeason.id}
          teams={teams}
          onClose={() => setShowAddPlayer(false)}
          onAdd={handleAddPlayer}
        />
      )}
      {showCreateSeason && (
        <CreateSeasonModal onClose={() => setShowCreateSeason(false)} onCreate={handleCreateSeason} />
      )}
    </div>
  );
}

function CreateSeasonModal({ onClose, onCreate }: { onClose: () => void; onCreate: (name: string) => void }) {
  const [name, setName] = useState("");

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-[#064428] rounded-xl max-w-sm w-full border border-[#0b5c33]">
        <div className="border-b border-[#0b5c33] px-5 py-3 flex items-center justify-between">
          <h2 className="text-lg font-bold text-white">Create Season</h2>
          <button onClick={onClose} className="text-xl text-white hover:bg-[#0b5c33] rounded-lg p-1 transition-colors">x</button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) onCreate(name.trim()); }} className="p-5 space-y-4">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Season 2026"
            className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-4 py-2.5 text-white placeholder-green-600 focus:outline-none focus:ring-2 focus:ring-[#0f9d3d]"
            autoFocus
          />
          <div className="flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 bg-[#012b1b] hover:bg-[#0b5c33] px-4 py-2.5 rounded-lg font-semibold text-white transition-colors text-sm">Cancel</button>
            <button type="submit" disabled={!name.trim()} className="flex-1 bg-[#0f9d3d] hover:bg-green-600 disabled:bg-green-900 px-4 py-2.5 rounded-lg font-semibold text-white transition-colors text-sm">Create</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddPlayerModal({
  leagueId,
  seasonId,
  teams,
  onClose,
  onAdd,
}: {
  leagueId: string;
  seasonId: string;
  teams: LeagueTeam[];
  onClose: () => void;
  onAdd: (player: Omit<LeaguePlayer, "id" | "created_at" | "updated_at">) => void;
}) {
  const [name, setName] = useState("");
  const [teamId, setTeamId] = useState(teams[0]?.id || "");
  const [role, setRole] = useState("Batsman");
  const [battingStyle, setBattingStyle] = useState("RHB");
  const [bowlingStyle, setBowlingStyle] = useState("");
  const [jersey, setJersey] = useState("");
  const [isCaptain, setIsCaptain] = useState(false);
  const [isVC, setIsVC] = useState(false);
  const [isWK, setIsWK] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !teamId) return;
    onAdd({
      league_id: leagueId,
      season_id: seasonId,
      team_id: teamId,
      player_name: name.trim(),
      player_id: null,
      role,
      batting_style: battingStyle,
      bowling_style: bowlingStyle || null,
      jersey_number: jersey ? parseInt(jersey) : null,
      is_captain: isCaptain,
      is_vice_captain: isVC,
      is_wicket_keeper: isWK,
      photo_url: null,
    });
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
      <div className="bg-[#064428] rounded-xl max-w-md w-full border border-[#0b5c33] max-h-[85vh] overflow-y-auto">
        <div className="border-b border-[#0b5c33] px-5 py-3 flex items-center justify-between sticky top-0 bg-[#064428] z-10">
          <h2 className="text-lg font-bold text-white">Add Player</h2>
          <button onClick={onClose} className="text-xl text-white hover:bg-[#0b5c33] rounded-lg p-1 transition-colors">x</button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-semibold text-green-300 mb-1">Player Name *</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-3 py-2 text-white placeholder-green-600 text-sm focus:outline-none focus:ring-2 focus:ring-[#0f9d3d]" required autoFocus />
          </div>
          <div>
            <label className="block text-xs font-semibold text-green-300 mb-1">Team *</label>
            <select value={teamId} onChange={(e) => setTeamId(e.target.value)} className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0f9d3d]">
              {teams.map(t => <option key={t.id} value={t.id}>{t.team_name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-green-300 mb-1">Role</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0f9d3d]">
                <option value="Batsman">Batsman</option>
                <option value="Bowler">Bowler</option>
                <option value="All-rounder">All-rounder</option>
                <option value="Keeper">Wicket Keeper</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-green-300 mb-1">Batting</label>
              <select value={battingStyle} onChange={(e) => setBattingStyle(e.target.value)} className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0f9d3d]">
                <option value="RHB">Right Hand</option>
                <option value="LHB">Left Hand</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-green-300 mb-1">Bowling Style</label>
              <select value={bowlingStyle} onChange={(e) => setBowlingStyle(e.target.value)} className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0f9d3d]">
                <option value="">N/A</option>
                <option value="RF">Right Fast</option>
                <option value="RFM">Right Fast Medium</option>
                <option value="RM">Right Medium</option>
                <option value="ROB">Right Off Break</option>
                <option value="RLB">Right Leg Break</option>
                <option value="LF">Left Fast</option>
                <option value="LFM">Left Fast Medium</option>
                <option value="LM">Left Medium</option>
                <option value="SLA">Slow Left Arm</option>
                <option value="LC">Left Chinaman</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-green-300 mb-1">Jersey #</label>
              <input type="number" value={jersey} onChange={(e) => setJersey(e.target.value)} placeholder="#" min="0" max="99" className="w-full bg-[#012b1b] border border-[#0b5c33] rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0f9d3d]" />
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
              <input type="checkbox" checked={isCaptain} onChange={(e) => setIsCaptain(e.target.checked)} className="accent-[#0f9d3d]" />Captain
            </label>
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
              <input type="checkbox" checked={isVC} onChange={(e) => setIsVC(e.target.checked)} className="accent-[#0f9d3d]" />Vice Captain
            </label>
            <label className="flex items-center gap-2 text-sm text-white cursor-pointer">
              <input type="checkbox" checked={isWK} onChange={(e) => setIsWK(e.target.checked)} className="accent-[#0f9d3d]" />Wicket Keeper
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 bg-[#012b1b] hover:bg-[#0b5c33] px-4 py-2.5 rounded-lg font-semibold text-white transition-colors text-sm">Cancel</button>
            <button type="submit" disabled={!name.trim() || !teamId} className="flex-1 bg-[#0f9d3d] hover:bg-green-600 disabled:bg-green-900 px-4 py-2.5 rounded-lg font-semibold text-white transition-colors text-sm">Add Player</button>
          </div>
        </form>
      </div>
    </div>
  );
}
